import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import FormatPercent from 'components/utils/Formatters/FormatPercent'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import type { ComponentProps, ReactNode } from 'react'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { TourV2DepartureDeposit } from './getTourV2DepartureDeposit'

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText'>

interface Props extends InheritedModalProps {
  summary: TourV2DepartureDeposit
}

export type TourDepositInfoModalResult = 'primary-action' | undefined

function formatDepositAmount(deposit: App.Tours.TourPaymentSchedule): ReactNode {
  return deposit.amountType === 'fixed_amount'
    ? <FormatCurrency value={deposit.amountValue} />
    : <FormatPercent value={deposit.amountValue} />
}

function TourDepositInfoModal({ summary, primaryActionText }: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<TourDepositInfoModalResult>()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const isTourDepositOnly = summary.source === 'tour-deposit'
  const isPaymentScheduleCopy = !isTourDepositOnly

  const depositAmountNode = formatDepositAmount(summary.deposit)

  const introCopy = isTourDepositOnly ? (
    <FormattedMessage
      id="-WeSZAAvjC-uvQB302lo"
      defaultMessage="We provide you flexibility to secure your tour with {depositAmount} upfront, and pay off the balance {balanceDueDays} days before departure."
      description="Explanation of the legacy tour deposit, showing the deposit amount and how many days before departure the remaining balance is charged"
      values={{ depositAmount: depositAmountNode, balanceDueDays: summary.balanceDueDays }}
    />
  ) : (
    <FormattedMessage
      id="mvm9rYf6tG2uLm95X4ii"
      defaultMessage="Through Payment Schedules, we provide you flexibility to secure your tour with {depositAmount} upfront, and pay off the balance across multiple payments. Available when booking at least {availableFromDays} days before departure."
      description="Payment schedule explanation with deposit amount and how many days before departure the schedule is available"
      values={{ depositAmount: depositAmountNode, availableFromDays: summary.availableFromDays }}
    />
  )

  const modalTitle = intl.formatMessage({
    id: 'Lcegh98pmJssWw5-Y4l3',
    defaultMessage: 'How it works',
    description: 'Title for the tour deposit information modal on the tour page',
  })

  return <Modal
    title={modalTitle}
    primaryActionText={primaryActionText}
    onPrimaryActionClick={primaryActionText ? handlePrimaryClick : undefined}
  >
    <Group direction="vertical" gap={16}>
      <div>
        <BodyText variant="medium" colour="light-foreground-alt">
          {introCopy}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'M8zNUCbMJdytM9MTr058',
          defaultMessage: 'Pay deposit now',
          description: 'Heading for the pay deposit now section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="JidA0zg0xRaQx26QohB6"
            defaultMessage="Secure your dates by paying {depositAmount} of your booking today. Add ons such as {programName} memberships, optional experiences, accommodations and travel protection must be paid upfront in addition to the {depositAmount} deposit."
            description="Explanation of deposit payment and additional upfront costs for tour booking"
            values={{
              depositAmount: depositAmountNode,
              programName: LUXURY_PLUS.PROGRAM_NAME,
            }}
          />
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'rCeZVv8XjRIrtL75_jOl',
          defaultMessage: 'Pay the rest later',
          description: 'Heading for the pay later section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'QoBJuwk5gDtiQYEc9564',
            defaultMessage: 'Payments will be automatically charged to your original payment method on each due date. Don’t worry, we’ll send you a reminder email 3 to 5 days before your payment is due.',
            description: 'Body text explaining automatic payment schedule and reminder emails',
          })}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: '0GJhyI55CG-k4sI3BiG5',
          defaultMessage: 'Service Fee',
          description: 'Heading for the service fee section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          {isPaymentScheduleCopy ? intl.formatMessage({
            id: '8HBqP77-ICe2Mkhnj04V',
            defaultMessage: 'The service fee paid as part of a Payment Schedule is non-refundable, even if cancellation occurs within 7 days of purchase.',
            description: 'Body text explaining non-refundable service fee policy',
          }) : intl.formatMessage({
            id: 'wEIw04Q83W8xIfOjYX6P',
            defaultMessage: 'The service fee paid as part of your deposit is non-refundable, even if cancellation occurs within 7 days of purchase.',
            description: 'Body text explaining that the service fee charged alongside a legacy tour deposit cannot be refunded',
          })}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{intl.formatMessage({
          id: 'H861zHNaaDoM9aVAZ9ZL',
          defaultMessage: 'Cancellation policy',
          description: 'Heading for the cancellation policy section in tour deposit modal',
        })}
        </Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'YT2G-h-4Wjv0VlmukeNo',
            defaultMessage: 'All deposits, service fee, and payments are non-refundable.',
            description: 'First paragraph of cancellation policy stating non-refundable payments',
          })}
          <br /><br />
          {isPaymentScheduleCopy ? intl.formatMessage({
            id: 'AzODaWBVos1wl5Ft5ja0',
            defaultMessage: 'Once you have made a payment as part of your Payment Schedule, that payment is non-refundable. Failure to complete any payment will result in the cancellation of your booking, and you will forfeit the entire amount you have paid.',
            description: 'Second paragraph of cancellation policy about payment forfeiture',
          }) : intl.formatMessage({
            id: '3ls_mx9gROlq7iHFSZqt',
            defaultMessage: 'Once you have paid your deposit, that payment is non-refundable. Failure to complete the remaining balance will result in the cancellation of your booking, and you will forfeit the entire amount you have paid.',
            description: 'Cancellation policy paragraph explaining that a legacy tour deposit is forfeited if the remaining balance is not paid',
          })}
          <br /><br />
          {intl.formatMessage(
            {
              id: 'grghnMDcOalIzVL3ANO8',
              defaultMessage: 'We may allow a cancellation for credit due to exceptional circumstances (and at our sole discretion). Where a cancellation/credit is not otherwise permitted and you still wish to cancel your booking, an additional service fee of {serviceFee} per person will apply (above and beyond the existing fees already paid pursuant to the payment schedules).',
              description: 'Third paragraph of cancellation policy about exceptional circumstances and additional fees',
            },
            { serviceFee: <FormatCurrency value={300} currency="AUD" /> },
          )}
        </BodyText>
      </div>
    </Group>
  </Modal>
}

export default TourDepositInfoModal
